import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Table, Badge, Button, Form, ProgressBar, Dropdown } from 'react-bootstrap';

const TestSuites = () => {
  const [showFilters, setShowFilters] = useState(false);

  // Mock data
  const testSuites = [
    { id: 'TS001', name: 'Authentication Suite', totalCases: 45, assignedTesters: ['John Doe', 'Jane Smith'], lastRun: '2025-12-10', passRate: 92, status: 'Passed' },
    { id: 'TS002', name: 'Payment Module Suite', totalCases: 32, assignedTesters: ['Mike Johnson'], lastRun: '2025-12-11', passRate: 78, status: 'Failed' },
    { id: 'TS003', name: 'Dashboard Suite', totalCases: 28, assignedTesters: ['Sarah Wilson', 'John Doe'], lastRun: '2025-12-09', passRate: 100, status: 'Passed' },
    { id: 'TS004', name: 'API Integration Suite', totalCases: 56, assignedTesters: ['Mike Johnson', 'Jane Smith'], lastRun: '2025-12-12', passRate: 85, status: 'In Progress' },
    { id: 'TS005', name: 'Mobile Responsive Suite', totalCases: 38, assignedTesters: ['Sarah Wilson'], lastRun: '2025-12-08', passRate: 95, status: 'Passed' }
  ];

  const stats = [
    { label: 'Total Suites', value: '42', icon: 'fa-layer-group', color: 'primary' },
    { label: 'Passed', value: '28', icon: 'fa-check-circle', color: 'success' },
    { label: 'Failed', value: '8', icon: 'fa-times-circle', color: 'danger' },
    { label: 'In Progress', value: '6', icon: 'fa-spinner', color: 'warning' }
  ];

  const getStatusVariant = (status) => {
    const variants = {
      'Passed': 'success',
      'Failed': 'danger',
      'In Progress': 'warning',
      'Not Run': 'secondary'
    };
    return variants[status] || 'secondary';
  };

  const getPassRateColor = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'danger';
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1 fw-bold">Test Suites</h4>
          <p className="text-muted mb-0">Organize test cases into executable suites</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary">
            <i className="fas fa-filter me-2"></i>Filters
          </Button>
          <Button variant="primary">
            <i className="fas fa-plus me-2"></i>Create Suite
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="g-3 mb-4">
        {stats.map((stat, index) => (
          <Col lg={3} md={6} key={index}>
            <Card className="c-card-1 h-100">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <div className={`d-flex align-items-center justify-content-center bg-${stat.color} bg-opacity-10 text-${stat.color} rounded-3`} style={{ width: '48px', height: '48px', fontSize: '1.25rem' }}>
                    <i className={`fas ${stat.icon}`}></i>
                  </div>
                  <div className="flex-grow-1">
                    <div className="fs-4 fw-bold mb-1">{stat.value}</div>
                    <div className="text-muted small">{stat.label}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Test Suites Table */}
      <div className="c-card-1">
        <div className="c-card-header-1">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>All Test Suites</div>
            <Form.Control 
              type="search" 
              placeholder="Search suites..." 
              className="form-control-sm"
              style={{ width: '250px' }}
            />
          </div>
        </div>
        <div className="c-card-body-1 p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="px-3 py-3 border-0">Suite ID</th>
                  <th className="px-3 py-3 border-0">Suite Name</th>
                  <th className="px-3 py-3 border-0">Total Cases</th>
                  <th className="px-3 py-3 border-0">Assigned Testers</th>
                  <th className="px-3 py-3 border-0">Pass Rate</th>
                  <th className="px-3 py-3 border-0">Last Run</th>
                  <th className="px-3 py-3 border-0">Status</th>
                  <th className="px-3 py-3 border-0 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {testSuites.map((suite) => (
                  <tr key={suite.id}>
                    <td className="px-3 py-3 align-middle">
                      <Link to={`/qc/test-suites/${suite.id}`} className="text-primary fw-semibold text-decoration-none">
                        {suite.id}
                      </Link>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <div className="fw-semibold text-dark">{suite.name}</div>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <span className="text-muted">{suite.totalCases} cases</span>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <div className="d-flex align-items-center gap-1">
                        {suite.assignedTesters.slice(0, 2).map((tester, idx) => (
                          <div 
                            key={idx}
                            className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                            style={{ width: '28px', height: '28px', fontSize: '11px' }}
                            title={tester}
                          >
                            {tester.split(' ').map(n => n[0]).join('')}
                          </div>
                        ))}
                        {suite.assignedTesters.length > 2 && (
                          <span className="text-muted small">+{suite.assignedTesters.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1" style={{ width: '100px' }}>
                          <ProgressBar 
                            now={suite.passRate} 
                            variant={getPassRateColor(suite.passRate)}
                            style={{ height: '6px' }}
                          />
                        </div>
                        <span className="text-muted small fw-semibold">{suite.passRate}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <span className="text-muted small">{suite.lastRun}</span>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <Badge bg={getStatusVariant(suite.status)}>
                        {suite.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 align-middle text-end">
                      <Dropdown align="end">
                        <Dropdown.Toggle variant="link" className="btn-sm text-muted p-0" style={{ boxShadow: 'none' }}>
                          <i className="fas fa-ellipsis-v"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item as={Link} to={`/qc/test-suites/${suite.id}`}>
                            <i className="fas fa-eye me-2"></i>View Details
                          </Dropdown.Item>
                          <Dropdown.Item>
                            <i className="fas fa-play me-2"></i>Run Suite
                          </Dropdown.Item>
                          <Dropdown.Item>
                            <i className="fas fa-edit me-2"></i>Edit
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item className="text-danger">
                            <i className="fas fa-trash me-2"></i>Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </>
  );
};

export default TestSuites;